/**
 * livekitUiSlice
 *
 * Cross-component awareness of SDK events surfaced by SessionEventSurface.
 * Components subscribe to whichever sub-tree they care about; the slice is
 * append-mostly so background updates don't stomp UI in flight.
 *
 * Keys:
 *   networkState[sessionId]    : 'good' | 'degraded' | 'poor' | 'recovering'
 *   transportFamily[sessionId] : 'interactive' | 'stream_socket'
 *   playbackClock[sessionId]   : { mediaTsMs, rate, playing, anchorTsMs? }
 *   detections[deviceCh]       : { latest, history[] }    (sliding window 20)
 *   snapshots[deviceCh]        : { snapshotUrl, timestampMs }
 *   deviceStatus[deviceId]     : DeviceHeartbeat
 *   signalingConnection        : 'connected' | 'disconnected' | 'reconnecting'
 *   sessionConflicts[sessionId]: { role, expiresAtMs }    (latest only)
 *
 * `deviceCh` = `${deviceId}_${channelId}` — one entry per camera channel.
 */
import { createSlice } from '@reduxjs/toolkit';

const DETECTION_WINDOW = 20;

const initialState = {
  networkState: {},
  transportFamily: {},
  playbackClock: {},
  detections: {},
  snapshots: {},
  deviceStatus: {},
  signalingConnection: 'disconnected',
  sessionConflicts: {},
};

const livekitUiSlice = createSlice({
  name: 'livekitUiSlice',
  initialState,
  reducers: {
    setNetworkState(state, action) {
      const { sessionId, state: q } = action.payload;
      state.networkState[sessionId] = q;
    },
    setTransportFamily(state, action) {
      const { sessionId, family } = action.payload;
      state.transportFamily[sessionId] = family;
    },
    setPlaybackClock(state, action) {
      // Throttled by SessionEventSurface (≤4 dispatches/s per sessionId).
      const { sessionId, mediaTsMs, rate, playing, anchorTsMs } = action.payload;
      state.playbackClock[sessionId] = { mediaTsMs, rate, playing, anchorTsMs };
    },
    pushDetection(state, action) {
      const { key, timestampMs, detections } = action.payload;
      const entry = state.detections[key] ?? { latest: null, history: [] };
      entry.latest = { timestampMs, detections };
      entry.history.push({ timestampMs, detections });
      if (entry.history.length > DETECTION_WINDOW) {
        entry.history.splice(0, entry.history.length - DETECTION_WINDOW);
      }
      state.detections[key] = entry;
    },
    setSnapshot(state, action) {
      const { key, snapshotUrl, timestampMs } = action.payload;
      state.snapshots[key] = { snapshotUrl, timestampMs: timestampMs ?? Date.now() };
    },
    setDeviceStatus(state, action) {
      const { deviceId, heartbeat } = action.payload;
      state.deviceStatus[deviceId] = heartbeat;
    },
    setSignalingConnection(state, action) {
      state.signalingConnection = action.payload.state;
    },
    setSessionConflict(state, action) {
      const { sessionId, role, expiresAtMs } = action.payload;
      state.sessionConflicts[sessionId] = { role, expiresAtMs };
    },
    clearSession(state, action) {
      const sid = action.payload;
      delete state.networkState[sid];
      delete state.transportFamily[sid];
      delete state.playbackClock[sid];
      delete state.sessionConflicts[sid];
    },
  },
});

export const {
  setNetworkState,
  setTransportFamily,
  setPlaybackClock,
  pushDetection,
  setSnapshot,
  setDeviceStatus,
  setSignalingConnection,
  setSessionConflict,
  clearSession,
} = livekitUiSlice.actions;

export default livekitUiSlice.reducer;
